import { useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { BaSeButton } from "@base/react";
import logo from "../../assets/images/logo-planejadora.svg";
import PlanningSelectionStep from "./steps/planningSelection";
import IndicatorsSelectionStep from "./steps/indicatorsSelection";
import LabResults from "./steps/results";
import ApiLab from "../../services/lab";
import OnboardingTooltip from "../onboarding-tooltip";
import { LAB_MOCK_DATA } from "../onboarding-tooltip/mock-data/labMock";
import './index.scss';
import {eventTag} from "../../services/utils";

const eventLabGA = {
  name: "comparison_laboratory",
  category: "comparison_laboratory",
  action: "click",
}

const apiLab = ApiLab();

const STEPS = {
  PLANNING: 0,
  INDICATORS: 1,
  RESULTS: 2
}

const ANCHOR_ID = "onboarding-bar-anchor";
const injectBarAnchor = () => {
  removeBarAnchor();
  const el = document.createElement("div");
  el.id = ANCHOR_ID;
  // Posições fixas, já que os dados mostrados no Onboarding são mockados e sempre os mesmos
  el.style.cssText = `
    position: fixed;
    top: 314px;
    left: 214px;
    width: 101px;
    height: 172px;
    pointer-events: none;
    z-index: -1;
  `;
  document.body.appendChild(el);
};

const removeBarAnchor = () => {
  const el = document.getElementById(ANCHOR_ID);
  if (el) el.remove();
};

const ComparisonLab = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(STEPS.PLANNING);
  const [selectedPlannings, setSelectedPlannings] = useState([]);
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [initialLabData, setInitialLabData] = useState({});
  const [runOnboarding, setRunOnboarding] = useState({ active: false, trigger: null });
  const [showInformationBox, setShowInformationBox] = useState(false);
  const savedStateRef = useRef(null);

  const startOnboarding = useCallback((trigger = null) => {
    if (trigger === "review") {
      savedStateRef.current = {
        plannings: selectedPlannings,
        indicators: selectedIndicators,
      };
      setSelectedPlannings([]);
      setSelectedIndicators([]);
    }
    setRunOnboarding({ active: true, trigger });
  }, [selectedPlannings, selectedIndicators]);

  const handleOnboardingReady = useCallback(() => startOnboarding("backend"), [startOnboarding]);

  const ONBOARDING_STEPS = useMemo(() => [
    {
      title: "Bem vindo(a)!",
      content: "Conheça o laboratório de comparação. Aqui você pode comparar diferentes planejamentos e seus indicadores.",
      target: null,
      location: ".lab-subtext-paragraph",
      placement: "center",
    },
    {
      title: "Selecione Planejamentos",
      content: "Escolha até 12 planejamentos para compor um cenário.",
      target: ".planning-selection-card:first-child",
      location: ".planning-selection-card:first-child",
      placement: "right",
      onNext: () => setCurrentStep(STEPS.INDICATORS),
    },
    {
      title: "Selecione indicadores",
      content: "Escolha os indicadores que gostaria de visualizar nesse cenário.",
      target: ".indicators-selection-card:first-child",
      location: ".indicators-selection-card:first-child",
      placement: "right",
      onNext: () => {
        setCurrentStep(STEPS.RESULTS);
        setInitialLabData(LAB_MOCK_DATA);
        setSelectedIndicators(["l", "lt"]);
      },
      onPrev: () => setCurrentStep(STEPS.PLANNING),
    },
    {
      title: "Explore Cenários",
      content: "Compare os indicadores dos planejamentos selecionados.",
      target: ".chart-wrapper",
      location: ".chart-wrapper",
      placement: "bottom",
      onNext: () => injectBarAnchor(),
      onPrev: () => {
        removeBarAnchor();
        setCurrentStep(STEPS.INDICATORS);
        setInitialLabData({});
        setSelectedIndicators([]);
      },
    },
    {
      title: "Edite seus cenários",
      content: "Selecione um cenário específico para atualizar as suas informações. Não se preocupe, nada do que for alterado aqui vai refletir nos dados do seu planejamento original.",
      target: `#${ANCHOR_ID}`,
      location: `#${ANCHOR_ID}`,
      placement: "right",
      onNext: () => {
        setShowInformationBox(true);
      },
      onPrev: () => {
        removeBarAnchor();
        injectBarAnchor();
        setShowInformationBox(false);
      },
    },
    {
      title: "Crie novos planejamentos",
      content: "Depois de finalizar suas comparações você pode escolher um cenário vencedor, e a partir dele, criar um novo planejamento.",
      target: ".information-box",
      location: ".information-box",
      placement: "bottom",
      onNext: () => {
        setShowInformationBox(false);
      },
      onPrev: () => {
        setShowInformationBox(false);
        injectBarAnchor();
      },
    },
  ], []);

  const compare = useCallback((data) => {
    apiLab.compare(data)
      .then((res) => {
        setInitialLabData(res.data)
        setCurrentStep(STEPS.RESULTS)
        eventTag(eventLabGA?.name, eventLabGA?.category, "submit", "Comparando")
      })
      .catch((err) => console.log(err))
  }, [])

  const handleNextStep = () => {
    if (currentStep === STEPS.INDICATORS) {
      compare({
        plan_ids: selectedPlannings.map((plan) => plan.id),
        indicators: selectedIndicators,
      })
    } else if (currentStep < STEPS.RESULTS) {
      setCurrentStep(currentStep + 1);
      eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, "Próximo")

    }
  }

  const handleBackStep = () => {
    eventTag(eventLabGA?.name, eventLabGA?.category, eventLabGA?.action, "Voltar")
    if (currentStep > STEPS.PLANNING) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate("/inicio");
    }
  }

  const proceedCheck = () => {
    switch (currentStep) {
      case STEPS.PLANNING:
        return selectedPlannings.length >= 2 && selectedPlannings.length <= 12;
      case STEPS.INDICATORS:
        return selectedIndicators.length >= 1;
      default:
        return false;
    }
  }

  const reviewOnboarding = () => {
    setCurrentStep(STEPS.PLANNING);
    startOnboarding("review");
    eventTag(eventLabGA?.name, "tutorial", eventLabGA?.action, "Rever onboarding")
  }

  const renderSubText = (step) => {
    switch (step) {
      case STEPS.PLANNING:
        return (
          <p className="lab-subtext-paragraph">
            A área de experimentação te dá a possibilidade de comparar os indicadores de diferentes planejamentos.
            Selecione os planejamentos que você quer comparar.
            <br />
            Encare os selecionados como diferentes cenários a serem comparados.
          </p>
        )
      case STEPS.INDICATORS:
        return (
          <p>
            A área de experimentação te dá a possibilidade de comparar os indicadores de diferentes planejamentos.
            <br />
            Selecione os indicadores que deseja comparar.
          </p>
        )
      case STEPS.RESULTS:
        return (
          <p>
            Clique na barra do planejamento do indicador desejado para mudar qualquer dado.
          </p>
        )
      default:
        return null;
    }
  }

  const renderHeader = () => {
    return (
      <div className="comparison-lab-page-header">
        <div className="comparison-lab-page-header-text">
          <div className="comparison-lab-page-header-text-logo">
            {currentStep === STEPS.RESULTS && <img src={logo} alt="Logotipo planejadora" />}
            <h2 className={currentStep === STEPS.RESULTS ? "large" : ""}>Laboratório de comparações</h2>
          </div>
          <BaSeButton
            type="secondary"
            onClick={handleBackStep}
            value="Voltar"
            color="#2E5DE6"
            isBold
            width={78}
          />
        </div>
        {renderSubText(currentStep)}
      </div>
    )
  }

  const togglePlanning = (planning) => {
    eventTag(eventLabGA?.name, "plannings", eventLabGA?.action, `Comparar: ${planning?.title}`)
    setSelectedPlannings(prev => {
      const exists = prev.find(p => p.id === planning.id);
      if (exists) return prev.filter(p => p.id !== planning.id);
      return [...prev, planning];
    });
  };

  const toggleIndicator = (indicatorIdentifier) => {
    setSelectedIndicators(prev =>
      prev.includes(indicatorIdentifier)
        ? prev.filter(id => id !== indicatorIdentifier)
        : [...prev, indicatorIdentifier]
    );
  };

  const toggleAll = (allIndicators) => {
    setSelectedIndicators(prev => prev.length === allIndicators.length ? [] : allIndicators.map(indicator => indicator.identifier))
  }

  const renderHelperText = () => {
    if (selectedPlannings.length > 12) {
      return <p className="danger-text">Selecione <strong>até 12</strong> planejamentos</p>
    }
    if (selectedPlannings.length < 2) {
      return <p className="danger-text">Selecione <strong>no mínimo 2</strong> planejamentos</p>
    }
    return null;
  }

  return (
    <div className="comparison-lab-page">
      <OnboardingTooltip
        steps={ONBOARDING_STEPS}
        run={runOnboarding.active}
        eventGA={eventLabGA}
        onFinish={() => {
          removeBarAnchor();
          if (runOnboarding.trigger === "review" && savedStateRef.current) {
            setSelectedPlannings(savedStateRef.current.plannings);
            setSelectedIndicators(savedStateRef.current.indicators);
            savedStateRef.current = null;
          } else {
            setSelectedPlannings([]);
            setSelectedIndicators([]);
          }
          setRunOnboarding({ active: false, trigger: null });
          setInitialLabData({});
          setCurrentStep(STEPS.PLANNING);
        }}
      />
      {currentStep !== STEPS.RESULTS && renderHeader()}
      <div className="comparison-lab-page-content">
        {currentStep === STEPS.PLANNING && (
          <PlanningSelectionStep
            selectedPlannings={selectedPlannings}
            togglePlanning={togglePlanning}
            onOnboardingReady={handleOnboardingReady}
            isOnboarding={runOnboarding.active}
            eventLabGA={eventLabGA}
          />
        )}
        {currentStep === STEPS.INDICATORS && (
          <IndicatorsSelectionStep
            selectedIndicators={selectedIndicators}
            toggleIndicator={toggleIndicator}
            toggleAll={toggleAll}
            eventLabGA={eventLabGA}
          />
        )}
        {currentStep === STEPS.RESULTS && (
          <LabResults
            renderHeader={renderHeader}
            selectedPlannings={selectedPlannings}
            selectedIndicators={selectedIndicators}
            initialLabData={initialLabData}
            showInformationBox={showInformationBox}
            eventLabGA={eventLabGA}
          />
        )}
        {currentStep !== STEPS.RESULTS && (
          <div className="comparison-lab-page-content-button">
            <BaSeButton
              type="primary"
              value="Rever onboarding"
              onClick={reviewOnboarding}
              color="#5C8FD6"
            />
            <div>
              {renderHelperText()}
              <BaSeButton
                type="primary"
                onClick={handleNextStep}
                value={currentStep === STEPS.PLANNING ? "Próximo" : "Comparar"}
                color="#2E5DE6"
                isDisabled={!proceedCheck()}
                isBold
                width={currentStep === STEPS.PLANNING ? 93 : 106}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default ComparisonLab;
